import ShareButton from 'components/buttons/Share';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import approx from 'approximate-number';
import {
  CardHeader as MuiCardHeader,
  CardContent as MuiCardContent,
  CardActions as MuiCardActions,
  Badge,
  Paper,
  Fab,
  colors,
} from '@mui/material';
import truncate from 'lodash/truncate';
import classnames from 'classnames/bind';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import strip from 'strip';
import { useAuthContext } from 'AuthContext';
import Card from 'components/Card';
import CardText from 'components/Card/Text';
import HTMLFragment from 'components/HTMLFragment';
import HeroCarouselMicro from 'containers/HeroCarousel/Micro';
import { getEventHashtags } from 'lib/eventUtils';
import Tooltip from 'ripple/Components/Tooltip';
import { Favorite, ExpandMore } from '@mui/icons-material';
import useIsMobile from 'lib/useIsMobile';
import { useTheme } from '@emotion/react';
import { useApolloClient } from '@apollo/client';
import styles from './EventCard.scss';
import { GET_PROFILE_INFO } from '../../../../schema/apolloQueries/commonQueries';

const cx = classnames.bind(styles);

const EventCard = ({
  compact,
  card,
  expand,
  event,
  expandHandler,
  setSharing,
  carouselItemClickListener,
  handleLike,
  eventData,
  setEventData,
  ...props
}) => {
  const theme = useTheme();
  const { handleIntercepted, interceptAction } = useAuthContext();
  const mobile = useIsMobile();
  const [liking, setLiking] = useState(false);

  const client = useApolloClient();

  const profileData = client.readQuery({
    query: GET_PROFILE_INFO,
  });

  const content = useMemo(() => {
    if (compact) {
      if (event.summary) {
        return event.summary;
      }
      return truncate(event.description, {
        length: 140,
        separator: ' ',
      });
    }
    return event.description;
  }, [compact, event.description, event.summary]);

  const updateLikedStatus = (eventId, newLikedStatus, count) => {
    const eventIndex = eventData?.events.findIndex(list => list.id === eventId);
    if (eventIndex !== -1) {
      const updatedEvents = [...eventData?.events];
      updatedEvents[eventIndex] = {
        ...updatedEvents[eventIndex],
        liked: newLikedStatus,
        likeCount: count,
      };
      setEventData({
        events: updatedEvents,
        totalCount: eventData.totalCount,
        __typename: eventData.__typename,
      });
    } else {
      setEventData(eventData);
    }
  };

  const filterLikeEvent = eventData?.events?.filter(
    list => list.id === event?.id,
  );

  const getFilterObj = Array.isArray(filterLikeEvent) && filterLikeEvent.length > 0
      ? filterLikeEvent[0]
      : {};

  const like = useCallback(() => {
    setLiking(true);
    handleLike({
      variables: {
        isLiked: !getFilterObj?.liked,
        id: Number(event?.id),
        model: 'event',
      },
    }).then(likeRes => {
      if (likeRes?.data?.likeOrDislikeSponsor === true) {
        updateLikedStatus(event?.id, true, getFilterObj.likeCount + 1);
      } else {
        updateLikedStatus(event?.id, false, getFilterObj.likeCount - 1);
      }
      setLiking(false);
    });
  }, [event, eventData, getFilterObj]);

  const onLike = () => {
    if (profileData?.getProfile?.loggedIn) {
      like();
    } else {
      interceptAction(`like-${event.id}`);
    }
  };

  const shareUrl = useMemo(
    () => `${process.env.APP_URL}/events/${event.slug}`,
    [event.slug],
  );

  const body = useMemo(() => {
    return (
      <Fragment>
        <HeroCarouselMicro
          playlist={event.heroAsset}
          compact
          itemClickListener={carouselItemClickListener}
        />
        {(compact || expand) && (
          <Paper>
            <MuiCardHeader
              classes={{
                root: styles.header,
                title: styles.title,
                avatar: styles.avatar,
              }}
              title={<HTMLFragment>{event.sponsorName}</HTMLFragment>}
            />
            <MuiCardContent classes={{ root: styles.container }}>
              <MuiCardActions
                className={cx({
                  actions: true,
                })}
              >
                <Tooltip
                  title={getFilterObj?.liked ? 'Unlike' : 'Like'}
                  arrow
                  placement='top'
                  size='medium'
                >
                  <Fab
                    size='small'
                    classes={{ root: styles.fabIcon }}
                    onClick={onLike}
                    disabled={liking}
                  >
                    <Badge
                      badgeContent={approx(getFilterObj?.likeCount)}
                      color='error'
                      max={1000}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                    >
                      <Favorite
                        style={
                          getFilterObj?.liked ? { color: colors.red[500] } : {}
                        }
                      />
                    </Badge>
                  </Fab>
                </Tooltip>
                <Tooltip title='Share' arrow placement='top' size='medium'>
                  <Fab
                    disabled={liking}
                    size='small'
                    classes={{ root: styles.fabIcon }}
                  >
                    <ShareButton
                      hashtags={getEventHashtags(event)}
                      small={false}
                      url={shareUrl}
                      iconStyle={{
                        width: 30,
                        height: 30,
                        marginTop: -3,
                        marginLeft: 0,
                        marginRight: 0,
                        transform: 'scaleX(-1)',
                        color: theme.palette.text.primary,
                      }}
                      primary
                      title={`${strip(event.sponsorName)} ${strip(event.name)}`}
                      setSharing={setSharing}
                    />
                  </Fab>
                </Tooltip>
                <div style={{ display: 'flex', flex: 1 }} />
                {compact && (
                  <Tooltip
                    title='Event Details'
                    arrow
                    placement='top'
                    size='medium'
                  >
                    <Fab
                      size='small'
                      onClick={expandHandler}
                      variant={mobile ? 'round' : 'extended'}
                      color='primary'
                      classes={{ root: styles.fabIcon }}
                    >
                      {!mobile && 'More'}
                      <ExpandMore />
                    </Fab>
                  </Tooltip>
                )}
              </MuiCardActions>
              <MuiCardActions>
                <MuiCardHeader
                  classes={{ root: styles.item, title: styles.title }}
                  title={`${event.milestoneLabel} ${format(
                    event.milestoneDate,
                    'MMMM D, YYYY',
                  )}.`}
                />
              </MuiCardActions>
              {expand && (
                <CardText noTopPadding style={{ color: '#fff!important' }}>
                  <HTMLFragment>{content}</HTMLFragment>
                </CardText>
              )}
            </MuiCardContent>
          </Paper>
        )}
      </Fragment>
    );
  }, [
    event,
    carouselItemClickListener,
    compact,
    expand,
    onLike,
    shareUrl,
    setSharing,
    expandHandler,
    mobile,
    content,
    liking,
    getFilterObj,
  ]);

  useEffect(() => {
    if (profileData?.getProfile?.loggedIn) {
      handleIntercepted(`like-${event.id}`, like);
    }
  }, [event.id, handleIntercepted, like, profileData?.getProfile?.loggedIn]);

  return (
    <Card
      {...props}
      className={cx({
        card,
        compact,
      })}
      classes={{ root: styles.eventCard }}
      noBottomPadding
    >
      {body}
    </Card>
  );
};

EventCard.propTypes = {
  compact: PropTypes.bool,
  card: PropTypes.bool,
  expand: PropTypes.bool,
  event: PropTypes.object.isRequired,
  expandHandler: PropTypes.func,
  setSharing: PropTypes.func,
  carouselItemClickListener: PropTypes.func,
  handleLike: PropTypes.func.isRequired,
  setEventData: PropTypes.any,
  eventData: PropTypes.any,
};

export default EventCard;
